import {inject, Injectable} from '@angular/core';
import {Token} from '@kilobytelab/types';
import {getFileInfo, mostrarError, StorageService, UiService} from '@kilobytelab/core';
import {LocalStorageService} from './storage.service';
import {PushService} from './push.service';
import {LoadingController, ModalController, NavController} from '@ionic/angular/standalone';
import {environment} from '../../environments/environment';
import {ApiAppService, ApiLoginService, TIMEOUT_MS} from '@kilobytelab/domain';
import {ConfigService} from './config.service';
import {ShareComponent} from '../component/share/share.component';
import {SyncService} from './sync.service';

@Injectable({
  providedIn: 'root'
})
export class UtilService {

  private apiApp = inject(ApiAppService);
  private apiLogin = inject(ApiLoginService);
  private storage = inject(StorageService);
  private localstorage = inject(LocalStorageService);
  private pushService = inject(PushService);
  private navCtrl = inject(NavController);
  private ui = inject(UiService);
  private loadingCtrl = inject(LoadingController);
  private configService = inject(ConfigService);
  private modalCtrl = inject(ModalController);
  private syncService = inject(SyncService);

  START = async (token: Token, loading?: HTMLIonLoadingElement, redirigir: boolean = true) => {
    try {
      await this.storage.setToken(token);
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('TIMEOUT_RED')), TIMEOUT_MS)
      );
      try {
        await Promise.race([this.syncService.sync(true), timeout])
      } catch (syncError) {
        if (syncError.message === 'TIMEOUT_RED') {
          console.warn('La sincronización tardó demasiado. Arrancando en modo offline/caché.')
        } else {
          throw syncError
        }
      }
      if (loading) await loading.dismiss();
      await this.initPushNotifications();
      if (redirigir) return this.navCtrl.navigateRoot('/home/agenda');
    } catch (err) {
      if (loading) await loading.dismiss();
      return this.ERROR(err)
    }
  }

  LOGOUT = async () => {
    if (!environment.production) return this.exit();
    const pushToken = await this.localstorage.getTokenPush();
    if (!pushToken) return this.exit();
    const supported = await this.pushService.areNotificationsSupported();
    if (!supported) return this.exit();
    await this.pushService.deleteToken();
    this.apiApp.setTokenPush('').catch(console.error);
    return this.exit()
  }

  ERROR = async (err: any) => {
    const resultado = mostrarError(err);
    if (resultado.action === 'LOGOUT') return this.LOGOUT();
    if (resultado.action === 'TOAST' && resultado.message) {
      return this.ui.mostrarToast(resultado.message, true)
    }
  }

  private exit = async () => {
    await Promise.all([
      this.storage.clearItems(),
      this.apiLogin.logout()
    ]);
    await this.navCtrl.navigateRoot('/login')
  }

  async initPushNotifications() {
    await this.pushService.requestPushNotificationsPermission();
    await this.pushService.notificacionesForeground()
  }

  soporte = () => window.open('https://wa.me/34644020488', '_blank');

  sinDatos = async (msg: string, back = false) => {
    const panel = await this.loadingCtrl.create({
      spinner: null,
      message: msg || 'Sin datos',
      duration: 2000
    });
    await panel.present();
    await panel.onDidDismiss();
    if (back) {
      await this.navCtrl.navigateBack('/home/agenda')
    }
  }

  goLogin = () => this.navCtrl.navigateRoot('/login');

  goRoot = () => this.navCtrl.navigateRoot('/home/agenda');

  go = (direccion: string) => this.navCtrl.navigateForward(direccion);

  share = async (url: string) => {
    if (!this.configService.isMobile) return this.openLink(url);
    const modal = await this.modalCtrl.create({
      component: ShareComponent,
      initialBreakpoint: 0.18,
      breakpoints: [0, 0.18, 0.25, 0.30]
    });
    await modal.present();
    const {data} = await modal.onDidDismiss();
    if (data) {
      if (data === 'open') return this.openLink(url);
      if (data === 'share') return this.shareUrl(url);
      if (data === 'save') return this.saveUrl(url);
    }
  }

  private openLink = (url: string) => window.open(url, '_blank', 'noopener, noreferrer');

  private shareUrl = async (url: string) => {
    const loading = await this.ui.load();
    await loading.present();
    try {
      const response = await fetch(url, {cache: 'default', mode: 'cors'});
      if (!response.ok) throw new Error('Error descargando imagen');
      const blob = await response.blob();
      const {cleanName, mimeType} = getFileInfo(url, blob);
      const file = new File([blob], cleanName, {type: mimeType});
      const shareData = {
        files: [file],
        title: 'BabyCloud',
        text: 'Te envío este documento de BabyCloud'
      };
      const nav = navigator as any;
      if (nav.canShare && nav.canShare(shareData)) {
        await nav.share(shareData)
      } else {
        throw new Error('No soportado')
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.message?.includes('canceled')) return;
      await this.ui.mostrarToast('No se pudo compartir, intentando guardar...', true);
      await this.saveUrl(url)
    } finally {
      await loading.dismiss()
    }
  }

  private saveUrl = async (url: string) => {
    const loading = await this.ui.load();
    await loading.present();
    try {
      const response = await fetch(url, {cache: 'default', mode: 'cors'});
      if (!response.ok) throw new Error('Error descargando imagen');
      const blob = await response.blob();
      const {cleanName} = getFileInfo(url, blob);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = cleanName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl)
      }, 100)
    } catch (err) {
      await this.ui.mostrarToast('Error al guardar el archivo', true)
    } finally {
      await loading.dismiss()
    }
  }

}